/* Copyright (c) 2019 - 2021. Verimatrix. All Rights Reserved.
   All information in this file is Verimatrix Confidential and Proprietary. */

import React, { useState } from "react";
import useForm from "react-hook-form";
import { Auth } from "@aws-amplify/auth";
import { passwordValidator } from "../../Authenticator/utils";
import { ValidationError } from "../../Authenticator";
import { MessageDialogParams } from "../../../types/MessageDialogParams";
import { Ico } from "../../Icon/Icon";
import ConfirmationDialog from "../../ConfirmationDialog";
import {
  Form,
  Button,
  Dimmer,
  Loader,
  Modal,
  Transition,
  Grid,
} from "semantic-ui-react";
import styles from "../MyAccount.module.css";
import cStyles from "../../Components.module.css";
import { useTranslation } from "react-i18next";

interface ChangePasswordDialogProps {
  setShowSetPassword: any;
}

const ChangePasswordDialog: React.FC<ChangePasswordDialogProps> = ({
  setShowSetPassword,
}) => {
  type FormData = {
    currentPassword: string;
    password: string;
  };

  const { t } = useTranslation(["changePassword", "common"]);

  const { register, handleSubmit, errors, getValues } = useForm<FormData>();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<MessageDialogParams | null>(null);
  const [confirmChangePassword, setConfirmChangePassword] = useState(false);
  const emptyInputs = {
    currentPassword: "",
    password: "",
    confirmPassword: "",
  };
  const [inputs, setInputs] = useState<FormData>(emptyInputs);
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setInputs((inputs) => ({ ...inputs, [name]: value }));
    if (message) {
      setMessage(null);
    }
  };

  const onChangePassword = () => {
    if (message) {
      return;
    }
    setLoading(true);
    setMessage(null);
    Auth.currentAuthenticatedUser()
      .then((user) => {
        return Auth.changePassword(
          user,
          inputs.currentPassword,
          inputs.password
        );
      })
      .then(() => {
        setConfirmChangePassword(true);
      })
      .catch((err) => {
        setMessage({
          title: t("change_password"),
          message: `${t("change_error")} ${err.message || err}`,
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Transition animation="fade in" duration="250">
      <Modal
        closeOnEscape={true}
        closeOnDimmerClick={false}
        size="small"
        open
        onClose={() => setShowSetPassword(false)}
      >
        {loading && (
          <Dimmer active={true}>
            <Loader active={true} size="large" />
          </Dimmer>
        )}

        <Form onSubmit={handleSubmit(onChangePassword)}>
          <input type="submit" hidden />

          <Grid stackable padded style={{ padding: "15px" }}>
            <Grid.Row>
              <Grid.Column width={14}>
                <span className={cStyles.title}>{t("change_password")}</span>
              </Grid.Column>

              <Grid.Column width={2} floated="right">
                <div
                  className={cStyles.closeButton}
                  onClick={() => setShowSetPassword(false)}
                >
                  <Ico name="cancel" style={{ color: "#ffffff", margin: 0 }} />
                </div>
              </Grid.Column>
            </Grid.Row>

            <Grid.Row>
              <Grid.Column width={4} textAlign="right" verticalAlign="middle">
                <label>{t("current")}</label>
              </Grid.Column>
              <Grid.Column
                width={12}
                textAlign="left"
                style={{ lineHeight: "normal" }}
              >
                <Form.Field required error={!!errors.currentPassword}>
                  <input
                    autoFocus
                    name="currentPassword"
                    key="currentPassword"
                    type="password"
                    className={styles.myAccountInput}
                    placeholder={t("enter_current")}
                    autoComplete="off"
                    ref={register({
                      required: true,
                    })}
                    onChange={handleInputChange}
                  />
                </Form.Field>
              </Grid.Column>
            </Grid.Row>

            <Grid.Row>
              <Grid.Column width={4} textAlign="right" verticalAlign="middle">
                <label>{t("new")}</label>
              </Grid.Column>
              <Grid.Column
                width={12}
                textAlign="left"
                style={{ lineHeight: "normal" }}
              >
                <Form.Field required error={!!errors.password}>
                  <input
                    name="password"
                    key="password"
                    type="password"
                    className={styles.myAccountInput}
                    placeholder={t("enter_new")}
                    autoComplete="off"
                    ref={register({
                      pattern: passwordValidator,
                      validate: (value) =>
                        value !== getValues().currentPassword ||
                        (t("must_be_different") as string),
                    })}
                    onChange={handleInputChange}
                  />
                </Form.Field>
              </Grid.Column>
            </Grid.Row>

            <Grid.Row style={{ padding: 0 }}>
              <Grid.Column
                width={4}
                textAlign="right"
                verticalAlign="middle"
              ></Grid.Column>
              <Grid.Column width={10} textAlign="left" stretched>
                <ValidationError
                  errors={errors}
                  name="password"
                  pointing="above"
                  internal={true}
                />
              </Grid.Column>
            </Grid.Row>

            <Grid.Row>
              <Grid.Column width={4} textAlign="right" verticalAlign="middle">
                <label>{t("confirm")}</label>
              </Grid.Column>
              <Grid.Column
                width={12}
                textAlign="left"
                style={{ lineHeight: "normal" }}
              >
                <Form.Field required error={!!errors.confirmPassword}>
                  <input
                    name="confirmPassword"
                    key="confirmPassword"
                    type="password"
                    className={styles.myAccountInput}
                    placeholder={t("confirm_new")}
                    autoComplete="off"
                    ref={register({
                      validate: (value) =>
                        value === getValues().password ||
                        (t("not_match") as string),
                    })}
                    onChange={handleInputChange}
                  />
                </Form.Field>
              </Grid.Column>
            </Grid.Row>

            <Grid.Row style={{ padding: 0 }}>
              <Grid.Column
                width={4}
                textAlign="right"
                verticalAlign="middle"
              ></Grid.Column>
              <Grid.Column width={10} textAlign="left" stretched>
                <ValidationError
                  errors={errors}
                  name="confirmPassword"
                  pointing="above"
                  internal={true}
                />
              </Grid.Column>
            </Grid.Row>

            <Grid.Row style={{ marginTop: "2rem" }}>
              <Grid.Column width={8}>
                <Button
                  secondary
                  className={cStyles.buttonSecond}
                  onClick={() => setShowSetPassword(false)}
                >
                  <Ico name="cancel" style={{ marginRight: "10px" }} />
                  {t("common:cancel")}
                </Button>
              </Grid.Column>

              <Grid.Column width={8} floated="right" textAlign="right">
                <Button
                  id="changePasswordButton"
                  primary
                  className={cStyles.buttonPrime}
                  type="submit"
                >
                  <Ico name="check" style={{ marginRight: "10px" }} />
                  {t("change_password")}
                </Button>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Form>

        {message && (
          <ConfirmationDialog
            title={message.title}
            message={message.message}
            onOk={() => {
              setMessage(null);
            }}
          />
        )}
        {confirmChangePassword && (
          <ConfirmationDialog
            title="Change Password"
            message="Password was changed successfully."
            onOk={() => {
              setConfirmChangePassword(false);
              setShowSetPassword(false);
            }}
          />
        )}
      </Modal>
    </Transition>
  );
};

export default ChangePasswordDialog;
